import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Button } from '../ui/Button';
import { BinAssignmentModal } from './BinAssignmentModal';
import type { WarehouseRack, WarehouseBin, WarehouseZone, WarehouseBinWithInventory, BinProductPreview } from '../../types/warehouse';
import { useAuth } from '../../contexts/AuthContext';
import { getBinsWithInventoryCounts } from '../../lib/services/binInventoryService';
import { getAdminId } from '../../utils/getAdminId';
import {
  Grid3X3,
  Box,
  Copy,
  Printer,
  X,
  Package,
  Loader2,
  RefreshCw,
  AlertCircle,
  ImageIcon,
} from 'lucide-react';

interface RackBinsPanelProps {
  rack: WarehouseRack;
  bins: WarehouseBin[];
  zone?: WarehouseZone | null;
  onClose: () => void;
  onPrintLabels?: (binIds: string[]) => void;
}

export const RackBinsPanel: React.FC<RackBinsPanelProps> = ({
  rack,
  bins: initialBins,
  zone,
  onClose,
  onPrintLabels,
}) => {
  const { user } = useAuth();
  const [selectedBins, setSelectedBins] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [bins, setBins] = useState<WarehouseBinWithInventory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedBinForAssignment, setSelectedBinForAssignment] = useState<string | null>(null);
  const prevInitialBinsRef = useRef<WarehouseBin[]>([]);

  const loadBinsWithInventory = useCallback(async () => {
    const adminId = getAdminId(user);
    if (!adminId) return;

    setLoading(true);
    setError(null);
    try {
      const binsWithCounts = await getBinsWithInventoryCounts(rack.id, adminId);
      if (binsWithCounts.length > 0) {
        setBins(binsWithCounts);
      } else if (initialBins.length > 0) {
        setBins(initialBins.map((b) => ({ ...b, inventory_count: 0 })));
      } else {
        setBins([]);
      }
    } catch (err) {
      console.error('Error loading bins:', err);
      setError('Failed to load bin data');
      if (initialBins.length > 0) {
        setBins(initialBins.map((b) => ({ ...b, inventory_count: 0 })));
      }
    } finally {
      setLoading(false);
    }
  }, [rack.id, user, initialBins]);

  useEffect(() => {
    loadBinsWithInventory();
  }, [loadBinsWithInventory]);

  useEffect(() => {
    if (
      initialBins.length > 0 &&
      JSON.stringify(initialBins.map(b => b.id).sort()) !==
      JSON.stringify(prevInitialBinsRef.current.map(b => b.id).sort())
    ) {
      prevInitialBinsRef.current = initialBins;
      if (bins.length === 0 && !loading) {
        setBins(initialBins.map((b) => ({ ...b, inventory_count: 0 })));
      }
    }
  }, [initialBins, bins.length, loading]);

  const binGrid = useMemo(() => {
    const grid: (WarehouseBinWithInventory | null)[][] = [];

    for (let level = rack.levels; level >= 1; level--) {
      const row: (WarehouseBinWithInventory | null)[] = [];
      for (let bay = 1; bay <= rack.bays; bay++) {
        const bin = bins.find(
          (b) => b.level_number === level && b.bay_index === bay
        );
        row.push(bin || null);
      }
      grid.push(row);
    }

    return grid;
  }, [bins, rack.levels, rack.bays]);

  const bayLetters = useMemo(() => {
    const letters: string[] = [];
    for (let i = 0; i < rack.bays; i++) {
      letters.push(String.fromCharCode(65 + i));
    }
    return letters;
  }, [rack.bays]);

  const stats = useMemo(() => {
    const expectedTotal = rack.levels * rack.bays;
    const actualTotal = bins.length;
    const total = actualTotal > 0 ? actualTotal : expectedTotal;
    const occupied = bins.filter((b) => b.inventory_count > 0).length;
    const empty = total - occupied;
    const utilizationPercent = total > 0 ? Math.round((occupied / total) * 100) : 0;
    return { total, occupied, empty, expectedTotal, utilizationPercent };
  }, [bins, rack.levels, rack.bays]);

  const handleSelectAll = () => {
    if (selectedBins.size === bins.length) {
      setSelectedBins(new Set());
    } else {
      setSelectedBins(new Set(bins.map((b) => b.id)));
    }
  };

  const handleBinSelect = (bin: WarehouseBinWithInventory, e: React.MouseEvent) => {
    if (e.shiftKey || e.ctrlKey || e.metaKey) {
      setSelectedBins((prev) => {
        const next = new Set(prev);
        if (next.has(bin.id)) {
          next.delete(bin.id);
        } else {
          next.add(bin.id);
        }
        return next;
      });
    } else {
      setSelectedBinForAssignment(bin.id);
    }
  };

  const handleCopyBinCode = (binCode: string) => {
    navigator.clipboard.writeText(binCode);
  };

  const handlePrintSelected = () => {
    if (onPrintLabels && selectedBins.size > 0) {
      onPrintLabels(Array.from(selectedBins));
    }
  };

  const handleInventoryChanged = () => {
    loadBinsWithInventory();
  };

  return (
    <>
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center">
                <Grid3X3 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  {rack.rack_code}
                  {rack.rack_name && (
                    <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                      - {rack.rack_name}
                    </span>
                  )}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {rack.levels} levels x {rack.bays} bays = {rack.levels * rack.bays} bins
                  {zone && (
                    <span className="ml-2">
                      <span
                        className="inline-block w-2 h-2 rounded-full mr-1"
                        style={{ backgroundColor: zone.color }}
                      />
                      {zone.zone_code}
                    </span>
                  )}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                Grid View
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  viewMode === 'list'
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                List View
              </button>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Hold Shift/Ctrl to multi-select
              </span>
              <button
                onClick={handleSelectAll}
                className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                {selectedBins.size === bins.length ? 'Deselect All' : 'Select All'}
              </button>
              {selectedBins.size > 0 && onPrintLabels && (
                <Button variant="secondary" size="sm" onClick={handlePrintSelected}>
                  <Printer className="h-4 w-4 mr-2" />
                  Print Labels ({selectedBins.size})
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
            </div>
          ) : viewMode === 'grid' ? (
            <div className="overflow-x-auto">
              <div className="inline-block min-w-full">
                <div className="flex items-end gap-1 mb-2 pl-12">
                  {bayLetters.map((letter) => (
                    <div
                      key={letter}
                      className="w-20 text-center text-xs font-medium text-gray-500 dark:text-gray-400"
                    >
                      Bay {letter}
                    </div>
                  ))}
                </div>

                {binGrid.map((row, rowIndex) => {
                  const levelNumber = rack.levels - rowIndex;
                  return (
                    <div key={rowIndex} className="flex items-center gap-1 mb-1">
                      <div className="w-10 text-right text-xs font-medium text-gray-500 dark:text-gray-400 pr-2">
                        L{levelNumber}
                      </div>
                      {row.map((bin, colIndex) => (
                        <BinCell
                          key={colIndex}
                          bin={bin}
                          isSelected={bin ? selectedBins.has(bin.id) : false}
                          onClick={(e) => bin && handleBinSelect(bin, e)}
                          onCopy={() => bin && handleCopyBinCode(bin.bin_code)}
                        />
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="space-y-1 max-h-[400px] overflow-y-auto">
              {bins
                .sort((a, b) => {
                  if (a.level_number !== b.level_number) {
                    return b.level_number - a.level_number;
                  }
                  return a.bay_index - b.bay_index;
                })
                .map((bin) => (
                  <BinListItem
                    key={bin.id}
                    bin={bin}
                    isSelected={selectedBins.has(bin.id)}
                    onClick={(e) => handleBinSelect(bin, e)}
                    onCopy={() => handleCopyBinCode(bin.bin_code)}
                  />
                ))}
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          {error && (
            <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
              <span className="text-sm text-amber-700 dark:text-amber-300">{error}</span>
              <button
                onClick={loadBinsWithInventory}
                className="ml-auto text-sm text-amber-700 dark:text-amber-300 hover:underline"
              >
                Retry
              </button>
            </div>
          )}
          <div className="flex items-center justify-between text-sm flex-wrap gap-2">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded border-2 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50" />
                <span className="text-gray-600 dark:text-gray-400">Empty ({stats.empty})</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded border-2 border-emerald-500 bg-emerald-500/10 flex items-center justify-center">
                  <ImageIcon className="h-2.5 w-2.5 text-emerald-500" />
                </div>
                <span className="text-gray-600 dark:text-gray-400">Has Items ({stats.occupied})</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded border-2 border-blue-500 bg-blue-100 dark:bg-blue-900/30" />
                <span className="text-gray-600 dark:text-gray-400">Selected</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {stats.occupied > 0 && (
                <span className="text-emerald-600 dark:text-emerald-400 font-medium">
                  {stats.utilizationPercent}% utilized
                </span>
              )}
              <span className="text-gray-500 dark:text-gray-400">
                {stats.total} bins
              </span>
              <button
                onClick={loadBinsWithInventory}
                disabled={loading}
                className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 disabled:opacity-50"
                title="Refresh bin data"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {selectedBinForAssignment && (
        <BinAssignmentModal
          binId={selectedBinForAssignment}
          warehouseId={rack.warehouse_id}
          onClose={() => setSelectedBinForAssignment(null)}
          onInventoryChanged={handleInventoryChanged}
        />
      )}
    </>
  );
};

interface BinCellProps {
  bin: WarehouseBinWithInventory | null;
  isSelected: boolean;
  onClick: (e: React.MouseEvent) => void;
  onCopy: () => void;
}

const BinImageThumbnail: React.FC<{ src: string; alt: string }> = ({ src, alt }) => {
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);

  if (failed || !src) {
    return (
      <div className="w-full h-full bg-emerald-600/80 flex items-center justify-center">
        <Package className="h-4 w-4 text-white/70" />
      </div>
    );
  }

  return (
    <>
      {!loaded && (
        <div className="absolute inset-0 bg-emerald-600/80 flex items-center justify-center">
          <Package className="h-4 w-4 text-white/70 animate-pulse" />
        </div>
      )}
      <img
        src={src}
        alt={alt}
        loading="lazy"
        crossOrigin="anonymous"
        onLoad={() => setLoaded(true)}
        onError={() => setFailed(true)}
        className={`w-full h-full object-contain transition-opacity duration-200 ${loaded ? 'opacity-100' : 'opacity-0'}`}
      />
    </>
  );
};

const BinPreviewContent: React.FC<{ items: BinProductPreview[]; totalCount: number }> = ({ items, totalCount }) => {
  const hasImages = items.some((p) => p.image_url);

  if (items.length === 1) {
    const item = items[0];
    if (item.image_url) {
      return (
        <div className="absolute inset-0 rounded overflow-hidden">
          <BinImageThumbnail src={item.image_url} alt={item.name} />
        </div>
      );
    }
    return (
      <div className="absolute inset-0 rounded overflow-hidden bg-emerald-500 flex items-center justify-center">
        <Package className="h-5 w-5 text-white/80" />
      </div>
    );
  }

  if (!hasImages) {
    return (
      <div className="absolute inset-0 rounded overflow-hidden bg-emerald-500 flex items-center justify-center">
        <Package className="h-5 w-5 text-white/80" />
      </div>
    );
  }

  const displayItems = items.slice(0, 4);
  const overflow = totalCount > 4 ? totalCount - 3 : 0;
  const gridItems = overflow > 0 ? displayItems.slice(0, 3) : displayItems;

  return (
    <div className="absolute inset-0 rounded overflow-hidden grid grid-cols-2 grid-rows-2 gap-px bg-gray-700/30">
      {gridItems.map((item, idx) => (
        <div key={idx} className="relative overflow-hidden">
          {item.image_url ? (
            <BinImageThumbnail src={item.image_url} alt={item.name} />
          ) : (
            <div className="w-full h-full bg-emerald-600/80 flex items-center justify-center">
              <Package className="h-3 w-3 text-white/70" />
            </div>
          )}
        </div>
      ))}
      {overflow > 0 && (
        <div className="relative overflow-hidden bg-gray-800/90 flex items-center justify-center">
          <span className="text-[10px] font-bold text-white">+{overflow}</span>
        </div>
      )}
      {gridItems.length < 4 && overflow === 0 && Array.from({ length: 4 - gridItems.length }).map((_, idx) => (
        <div key={`empty-${idx}`} className="relative overflow-hidden bg-emerald-600/60 flex items-center justify-center">
          <Package className="h-3 w-3 text-white/50" />
        </div>
      ))}
    </div>
  );
};

const BinCell = React.memo<BinCellProps>(({ bin, isSelected, onClick, onCopy }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  if (!bin) {
    return (
      <div className="w-20 h-16 rounded border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 flex items-center justify-center">
        <span className="text-xs text-gray-400">-</span>
      </div>
    );
  }

  const hasItems = bin.inventory_count > 0;
  const previews = bin.preview_items || [];

  return (
    <div
      className="relative"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <button
        onClick={onClick}
        className={`w-20 h-16 rounded border-2 transition-all relative overflow-hidden ${
          isSelected
            ? 'border-blue-500 ring-1 ring-blue-400'
            : hasItems
            ? 'border-emerald-500 hover:border-emerald-400 hover:shadow-md'
            : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 hover:border-emerald-400 hover:bg-gray-100 dark:hover:bg-gray-700/50'
        }`}
      >
        {hasItems && <BinPreviewContent items={previews} totalCount={bin.inventory_count} />}

        {isSelected && hasItems && (
          <div className="absolute inset-0 bg-blue-500/30 rounded" />
        )}

        <div className={`absolute inset-x-0 bottom-0 px-1 py-0.5 ${
          hasItems ? 'bg-gradient-to-t from-black/70 via-black/40 to-transparent' : ''
        }`}>
          <span className={`text-[10px] font-semibold block truncate ${
            isSelected && !hasItems
              ? 'text-blue-800 dark:text-blue-200'
              : hasItems
              ? 'text-white drop-shadow-sm'
              : 'text-gray-600 dark:text-gray-400'
          }`}>
            {bin.bin_code}
          </span>
          {hasItems && (
            <span className="text-[8px] text-white/80 drop-shadow-sm">
              {bin.inventory_count} item{bin.inventory_count !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </button>

      {hasItems && !isSelected && (
        <div className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 rounded-full flex items-center justify-center shadow-sm z-10">
          <Package className="h-2.5 w-2.5 text-white" />
        </div>
      )}

      {showTooltip && (
        <div className="absolute z-20 bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2.5 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-xl min-w-[200px]">
          <div className="font-semibold text-sm">{bin.bin_code}</div>
          <div className="text-gray-400 mt-0.5">
            Level {bin.level_number} | Bay {bin.bay_letter}
          </div>

          {hasItems && previews.length > 0 && (
            <div className="mt-2 space-y-1.5 border-t border-gray-700 dark:border-gray-600 pt-2">
              {previews.slice(0, 3).map((item, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded overflow-hidden flex-shrink-0 bg-gray-800">
                    {item.image_url ? (
                      <img src={item.image_url} alt={item.name} crossOrigin="anonymous" className="w-full h-full object-contain" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-emerald-800/50">
                        <Package className="h-3.5 w-3.5 text-emerald-400/60" />
                      </div>
                    )}
                  </div>
                  <span className="text-gray-200 truncate max-w-[140px]">{item.name}</span>
                </div>
              ))}
              {bin.inventory_count > 3 && (
                <div className="text-gray-500 text-[10px] pl-10">
                  +{bin.inventory_count - 3} more
                </div>
              )}
            </div>
          )}

          {!hasItems && (
            <div className="mt-1">
              <span className="text-gray-400">Empty - Click to assign items</span>
            </div>
          )}

          {hasItems && previews.length === 0 && (
            <div className="mt-1">
              <span className="text-emerald-400">
                {bin.inventory_count} product{bin.inventory_count !== 1 ? 's' : ''} assigned
              </span>
            </div>
          )}

          <button
            onClick={(e) => {
              e.stopPropagation();
              onCopy();
            }}
            className="mt-2 flex items-center gap-1 text-blue-400 hover:text-blue-300"
          >
            <Copy className="h-3 w-3" />
            Copy code
          </button>
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full">
            <div className="border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
          </div>
        </div>
      )}
    </div>
  );
}, (prev, next) => (
  prev.bin?.id === next.bin?.id &&
  prev.bin?.inventory_count === next.bin?.inventory_count &&
  prev.isSelected === next.isSelected &&
  prev.bin?.preview_items === next.bin?.preview_items
));

interface BinListItemProps {
  bin: WarehouseBinWithInventory;
  isSelected: boolean;
  onClick: (e: React.MouseEvent) => void;
  onCopy: () => void;
}

const BinListItem: React.FC<BinListItemProps> = ({ bin, isSelected, onClick, onCopy }) => {
  const hasItems = bin.inventory_count > 0;
  const previews = bin.preview_items || [];
  const firstImage = previews.find((p) => p.image_url)?.image_url || null;

  return (
    <div
      onClick={onClick}
      className={`flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition-colors ${
        isSelected
          ? 'bg-blue-100 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-700'
          : hasItems
          ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100 dark:hover:bg-emerald-900/30'
          : 'bg-gray-50 dark:bg-gray-800/50 border border-transparent hover:bg-gray-100 dark:hover:bg-gray-700/50'
      }`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded overflow-hidden flex-shrink-0 relative ${
          !hasItems ? 'bg-gray-100 dark:bg-gray-800 flex items-center justify-center' : ''
        }`}>
          {hasItems && firstImage ? (
            <>
              <img
                src={firstImage}
                alt={previews[0]?.name || ''}
                className="w-full h-full object-contain"
                loading="lazy"
              />
              {bin.inventory_count > 1 && (
                <div className="absolute -bottom-0.5 -right-0.5 min-w-[16px] h-4 bg-emerald-600 rounded-full flex items-center justify-center px-1 shadow-sm">
                  <span className="text-[9px] font-bold text-white">{bin.inventory_count}</span>
                </div>
              )}
            </>
          ) : hasItems ? (
            <div className="w-full h-full bg-emerald-500 flex items-center justify-center rounded">
              <Package className="h-4 w-4 text-white" />
            </div>
          ) : (
            <Box
              className={`h-4 w-4 ${
                isSelected
                  ? 'text-blue-600 dark:text-blue-400'
                  : 'text-gray-400 dark:text-gray-500'
              }`}
            />
          )}
        </div>
        <div>
          <span className="font-medium text-gray-900 dark:text-gray-100">{bin.bin_code}</span>
          <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
            Level {bin.level_number} | Bay {bin.bay_letter}
          </span>
          {hasItems && (
            <span className="ml-2 text-xs text-emerald-600 dark:text-emerald-400 font-medium">
              ({bin.inventory_count} item{bin.inventory_count !== 1 ? 's' : ''})
            </span>
          )}
          {hasItems && previews.length > 0 && (
            <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate max-w-[200px]">
              {previews.map((p) => p.name).join(', ')}
            </div>
          )}
        </div>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onCopy();
        }}
        className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        title="Copy bin code"
      >
        <Copy className="h-4 w-4" />
      </button>
    </div>
  );
};
